import { cn } from '@/lib/cn'
import { EMPTY, daysUntil, formatDateShort, formatDueLabel } from '@/lib/format'
import type { CaseStatus, DeadlineLike } from '../types'
import { deadlineDate, deadlineTitle } from '../hooks'

/** Podpowiedź pod kreską, gdy sprawa nie ma terminów (jak w mockupie rejestru). */
function hintFor(status: CaseStatus): string {
  switch (status) {
    case 'zawieszona':
      return 'postępowanie zawieszone'
    case 'zakonczona':
      return 'brak terminów'
    default:
      return 'brak terminów'
  }
}

export interface DeadlineCellProps {
  deadline?: DeadlineLike | null
  status: CaseStatus
  /** `false`, gdy moduł terminów jest wyłączony/niedostępny — wtedy tylko „—". */
  moduleAvailable: boolean
}

/**
 * Kolumna NAJBLIŻSZY TERMIN. Dane pochodzą z modułu `deadlines`, który może
 * jeszcze nie istnieć — wtedy komórka pokazuje samo „—".
 */
export function DeadlineCell({ deadline, status, moduleAvailable }: DeadlineCellProps) {
  const date = deadline ? deadlineDate(deadline) : null

  if (!deadline || !date) {
    return (
      <div className="leading-tight">
        <span className="text-muted">{EMPTY}</span>
        {moduleAvailable ? <p className="mt-0.5 text-small text-muted">{hintFor(status)}</p> : null}
      </div>
    )
  }

  // Mockup: „za 11 dni" jest czerwone, „za 14 / 21 / 38 dni" — bursztynowe.
  // Termin PO UPŁYWIE (`days < 0`) nazywamy po imieniu: `/api/deadlines/upcoming` zwraca
  // go tak samo jak przyszły, więc kolumna i chip na karcie sprawy mówią to samo.
  const days = daysUntil(date)
  const overdue = days !== null && days < 0
  const urgent = days !== null && days <= 12
  return (
    <div className="w-[176px] max-w-full leading-tight">
      {/* Mockup trzyma „tytuł · data" w JEDNEJ linii — długi tytuł jest przycinany,
          data i tak musi zostać widoczna. */}
      <p
        className={cn(
          'flex items-baseline gap-1 text-base font-medium',
          urgent ? 'text-danger' : 'text-warning',
        )}
        title={deadlineTitle(deadline)}
      >
        <span className="min-w-0 truncate">{deadlineTitle(deadline)}</span>
        <span className="shrink-0">· {formatDateShort(date)}</span>
      </p>
      <p className={cn('mt-0.5 text-small', overdue ? 'font-medium text-danger' : 'text-muted')}>
        {overdue ? `po terminie · ${formatDueLabel(date)}` : formatDueLabel(date)}
      </p>
    </div>
  )
}
